/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { isNil } from 'lodash';
import { shallowEqual, useSelector } from 'react-redux';
import { PointProps } from 'victory';
import FlightLegServiceAlerts, { ServiceAlert } from '../../../../shared/entities/flightLegServiceAlerts';
import {
  selectMissionPlanLandingMarker,
  selectMissionPlanPathMarkers,
  selectMissionPlanTakeoffMarker,
} from '../../../../store';
import {
  getLandingMarkerSvg,
  getMarkerImageForServiceAlert,
  getTakeoffMarkerSvg,
} from '../../../fleet/MptFlightPath/MptServiceAlertsContainer';

const TAKEOFF_LANDING_WH = 20;
const OTHER_WH = 24;

export enum MarkerType {
  TAKEOFF = 'Takeoff',
  LANDING = 'Landing',
  OTHER = 'Other',
}

interface ServiceAlertMarkerProps extends PointProps {
  markerIndex: number;
  flightLegServiceAlert: FlightLegServiceAlerts;
  mapId: string;
  events?: any;
}

const ServiceAlertMarker = (props: ServiceAlertMarkerProps) => {
  const { markerIndex, datum, events, flightLegServiceAlert, mapId } = props;
  let { x, y } = props;

  const takeoffMarker = useSelector(selectMissionPlanTakeoffMarker(mapId), shallowEqual);
  const landingMarker = useSelector(selectMissionPlanLandingMarker(mapId), shallowEqual);
  const pathMarkers = useSelector(selectMissionPlanPathMarkers(mapId), shallowEqual);

  const [type, setType] = useState<MarkerType>(MarkerType.OTHER);

  x = x ? x : 0;
  y = y ? y : 0;

  useEffect(() => {
    if (datum.y === 0) {
      setType(MarkerType.TAKEOFF);
    } else if (flightLegServiceAlert && datum.y === flightLegServiceAlert?.durationMin) {
      setType(MarkerType.LANDING);
    } else {
      setType(MarkerType.OTHER);
    }
  }, [datum.y, flightLegServiceAlert]);

  const getSvgIcon = () => {
    switch (type) {
      case MarkerType.TAKEOFF:
        return getTakeoffMarkerSvg();
      case MarkerType.LANDING:
        return getLandingMarkerSvg();
      default:
        const serviceAlert =
          flightLegServiceAlert &&
          flightLegServiceAlert.serviceAlerts?.find(
            (serviceAlert: ServiceAlert) => serviceAlert.timeSinceTakeOffMin === datum.y,
          );
        return serviceAlert ? getMarkerImageForServiceAlert(serviceAlert) : '';
    }
  };

  const customMouseOverEvent = () => {
    switch (type) {
      case MarkerType.TAKEOFF:
        if (takeoffMarker) {
          google.maps.event.trigger(takeoffMarker, 'mouseover');
        }
        break;
      case MarkerType.LANDING:
        if (landingMarker) {
          google.maps.event.trigger(landingMarker, 'mouseover');
        }
        break;
      default:
        // (markerIndex - 1) b/c markerIndex includes takeoff marker (markerIndex = 0)
        // and pathMarkers does not include takeoff marker (and landing marker)
        if (!isNil(pathMarkers) && !isNil(pathMarkers[markerIndex - 1])) {
          google.maps.event.trigger(pathMarkers[markerIndex - 1], 'mouseover');
        }
    }

    events?.onMouseOver();
  };

  const customMouseOutEvent = () => {
    switch (type) {
      case MarkerType.TAKEOFF:
        if (takeoffMarker) {
          google.maps.event.trigger(takeoffMarker, 'mouseout');
        }
        break;
      case MarkerType.LANDING:
        if (landingMarker) {
          google.maps.event.trigger(landingMarker, 'mouseout');
        }
        break;
      default:
        if (!isNil(pathMarkers) && !isNil(pathMarkers[markerIndex - 1])) {
          google.maps.event.trigger(pathMarkers[markerIndex - 1], 'mouseout');
        }
    }

    events?.onMouseOut();
  };

  return (
    <React.Fragment>
      <foreignObject
        width={'100%'}
        height={'100%'}
        x={type === MarkerType.OTHER ? x - OTHER_WH / 2 : x - TAKEOFF_LANDING_WH / 2}
        y={type === MarkerType.OTHER ? y : y + 2}
      >
        <img
          src={getSvgIcon()}
          alt="Service Alert"
          {...events}
          onMouseOver={customMouseOverEvent}
          onMouseOut={customMouseOutEvent}
        />
      </foreignObject>
    </React.Fragment>
  );
};

export default ServiceAlertMarker;
